import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { ShieldCheck, ShieldAlert, Copy, Loader2, FileCode2, FileText } from "lucide-react";

export default function SiteOwnership({ site, onVerified }) {
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("meta");
  const token = site?.verify_token || "";
  const metaTag = `<meta name="blinxd-verify" content="${token}">`;
  const fileName = "blinxd-verify.txt";

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/site/ownership/verify");
      if (data.owner_verified) {
        toast.success("Ownership verified — you're in! 🎉");
        onVerified && (await onVerified());
      } else {
        toast.error(data.message || "Token not found on your site yet. Try again shortly.", { duration: 8000 });
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (site?.owner_verified) {
    return (
      <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-4 flex items-center gap-3" data-testid="ownership-verified">
        <div className="h-9 w-9 rounded-lg bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center">
          <ShieldCheck size={17} className="text-emerald-400" />
        </div>
        <div>
          <div className="text-sm font-medium text-emerald-300">Ownership verified</div>
          <div className="text-[12px] text-muted-foreground">You've proven you control <span className="font-mono">{site.url}</span>. You can swap freely.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-6" data-testid="ownership-pending">
      <div className="flex items-center gap-2 text-amber-400">
        <ShieldAlert size={18} />
        <h3 className="font-serif font-semibold text-lg text-foreground">Verify you own {site?.url}</h3>
      </div>
      <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
        This stops people claiming sites they don't control. Add <span className="text-foreground font-medium">either</span> proof
        below to your site, then hit verify. You only do this once.
      </p>

      <div className="mt-4 flex gap-2">
        <button onClick={() => setTab("meta")} data-testid="ownership-tab-meta"
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-mono transition-colors ${tab === "meta" ? "border-primary/40 bg-primary/10 text-primary" : "border-white/10 bg-white/5 text-muted-foreground hover:text-foreground"}`}>
          <FileCode2 size={13} /> Meta tag
        </button>
        <button onClick={() => setTab("file")} data-testid="ownership-tab-file"
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-mono transition-colors ${tab === "file" ? "border-primary/40 bg-primary/10 text-primary" : "border-white/10 bg-white/5 text-muted-foreground hover:text-foreground"}`}>
          <FileText size={13} /> HTML file
        </button>
      </div>

      {tab === "meta" ? (
        <div className="mt-3">
          <div className="text-[12px] text-muted-foreground mb-1.5">Paste this into your homepage <span className="font-mono text-foreground">&lt;head&gt;</span>:</div>
          <div className="flex items-stretch gap-2">
            <code data-testid="ownership-meta-tag" className="flex-1 min-w-0 rounded-lg bg-zinc-950 border border-white/10 px-3 py-2.5 font-mono text-[12px] text-emerald-300 overflow-x-auto whitespace-nowrap">{metaTag}</code>
            <Button onClick={() => copy(metaTag, "Meta tag")} variant="outline" size="sm" data-testid="ownership-copy-meta" className="rounded-lg border-white/10 bg-white/5 hover:bg-white/10 shrink-0">
              <Copy size={14} />
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <div>
            <div className="text-[12px] text-muted-foreground mb-1.5">1. Create a file named <span className="font-mono text-foreground">{fileName}</span> containing this token:</div>
            <div className="flex items-stretch gap-2">
              <code data-testid="ownership-token" className="flex-1 min-w-0 rounded-lg bg-zinc-950 border border-white/10 px-3 py-2.5 font-mono text-[12px] text-emerald-300 overflow-x-auto whitespace-nowrap">{token}</code>
              <Button onClick={() => copy(token, "Token")} variant="outline" size="sm" data-testid="ownership-copy-token" className="rounded-lg border-white/10 bg-white/5 hover:bg-white/10 shrink-0">
                <Copy size={14} />
              </Button>
            </div>
          </div>
          <div className="text-[12px] text-muted-foreground">
            2. Upload it so it's reachable at <span className="font-mono text-foreground">{site?.url}/.well-known/{fileName}</span> (or your site root).
          </div>
        </div>
      )}

      <Button onClick={verify} disabled={busy} data-testid="ownership-verify-button"
        className="mt-5 rounded-full bg-primary text-white hover:bg-blue-600 h-11 px-6 active:scale-95 transition-transform disabled:opacity-50">
        {busy ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Checking your site…</>) : (<><ShieldCheck size={16} className="mr-2" /> Verify ownership</>)}
      </Button>
    </div>
  );
}
